import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  Bot,
  Package,
  Clock,
  Mail,
  Phone,
  MapPin,
  Plus,
  CreditCard,
  Settings,
} from 'lucide-react';
import { 
  getServiceBusiness, 
  createServiceAgent, 
  addPackageToBusiness, 
  assignSubscriptionToAgent,
  getServicePlans,
  getServicePackages
} from '../../lib/api';
import { formatDate, formatMinutes } from '../../lib/utils';
import { formatCount, getAgentPlanName, getRemainingMessageCount } from '../../lib/subscriptionQuota';

export default function ServiceBusinessDetailPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [agents, setAgents] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [packages, setPackages] = useState([]);
  const [minuteBalance, setMinuteBalance] = useState(null);
  const [plans, setPlans] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [agentForm, setAgentForm] = useState({
    name: '',
    industry_type: 'general',
    phone_number: '',
  });
  
  const [packageForm, setPackageForm] = useState({
    package_id: '',
    minutes: '',
    notes: '',
  });
  
  const [subscriptionPlanId, setSubscriptionPlanId] = useState('');

  const loadBusiness = async () => {
    try {
      setLoading(true);
      const [businessRes, plansRes, packagesRes] = await Promise.all([
        getServiceBusiness(businessId),
        getServicePlans(),
        getServicePackages()
      ]);
      
      setBusiness(businessRes.data.business);
      setAgents(businessRes.data.agents || []);
      setSubscription(businessRes.data.subscription);
      setPackages(businessRes.data.packages || []);
      setMinuteBalance(businessRes.data.minute_balance);
      setPlans(plansRes.data.plans || []);
      setAvailablePackages(packagesRes.data.packages || []);
    } catch (error) {
      toast.error('Failed to load business');
      navigate('/service/businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusiness();
  }, [businessId]);

  const handleCreateAgent = async () => {
    if (!agentForm.name.trim()) {
      toast.error('Agent name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await createServiceAgent({
        customer_id: businessId,
        name: agentForm.name,
        industry_type: agentForm.industry_type,
        phone_number: agentForm.phone_number,
      });
      const createdAgentId = response?.data?.agent_id || response?.data?.agent?.agent_id;
      if (!createdAgentId) {
        throw new Error('Agent ID not returned');
      }
      toast.success('Agent created. Configure JSON and Twilio next.');
      setShowAgentDialog(false);
      setAgentForm({ name: '', industry_type: 'general', phone_number: '' });
      navigate(`/service/agents/${createdAgentId}/configure?fromCreate=1`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPackage = async () => {
    const minutes = packageForm.package_id ? null : parseInt(packageForm.minutes);
    
    if (!packageForm.package_id && (!minutes || minutes <= 0)) {
      toast.error('Select a package or enter minutes');
      return;
    }
    
    setSaving(true);
    try {
      await addPackageToBusiness(businessId, {
        package_id: packageForm.package_id || null,
        minutes: minutes,
        notes: packageForm.notes,
      });
      toast.success('Package added');
      setShowPackageDialog(false);
      setPackageForm({ package_id: '', minutes: '', notes: '' });
      loadBusiness();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add package');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignSubscription = async () => {
    if (!selectedAgent || !subscriptionPlanId) {
      toast.error('Please select a plan');
      return;
    }
    
    setSaving(true);
    try {
      await assignSubscriptionToAgent(selectedAgent.agent_id, subscriptionPlanId);
      toast.success('Subscription assigned');
      setShowSubscriptionDialog(false);
      setSelectedAgent(null);
      setSubscriptionPlanId('');
      loadBusiness();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign subscription');
    } finally {
      setSaving(false);
    }
  };

  const openSubscriptionDialog = (agent) => {
    setSelectedAgent(agent);
    setSubscriptionPlanId('');
    setShowSubscriptionDialog(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card><CardContent className="p-6"><Skeleton className="h-48" /></CardContent></Card>
      </div>
    );
  }

  if (!business) return null;

  return (
    <div className="space-y-6" data-testid="service-business-detail">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" onClick={() => navigate('/service/businesses')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Businesses
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate(`/service/businesses/${businessId}/twilio`)}
        >
          <Settings className="w-4 h-4 mr-2" />
          Twilio Config
        </Button>
      </div>

      {/* Business Info */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-amber-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900">{business.name}</h1>
              {business.company_name && (
                <p className="text-lg text-slate-600">{business.company_name}</p>
              )}
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {business.email}
                </span>
                {business.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {business.phone}
                  </span>
                )}
                {(business.city || business.state) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {[business.address, business.city, business.state, business.postcode].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>
            {minuteBalance && (
              <div className="text-right">
                <p className="text-sm text-slate-500">Minutes Balance</p>
                <p className="text-2xl font-bold text-amber-600">{formatMinutes(minuteBalance.total_remaining || 0)}</p>
                <p className="text-xs text-slate-400">
                  Used: {formatMinutes(minuteBalance.total_used || 0)}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="agents">
        <TabsList>
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            Agents ({agents.length})
          </TabsTrigger>
          <TabsTrigger value="packages" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Packages ({packages.length})
          </TabsTrigger>
        </TabsList>

        {/* Agents Tab */}
        <TabsContent value="agents" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Voice Agents</CardTitle>
              <Button onClick={() => setShowAgentDialog(true)} size="sm" className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Agent
              </Button>
            </CardHeader>
            <CardContent>
              {agents.length === 0 ? (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No agents yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {agents.map((agent) => (
                    <div
                      key={agent.agent_id}
                      className="p-4 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Bot className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{agent.name}</h4>
                            <p className="text-sm text-slate-500">
                              ID: {agent.business_id} • {agent.industry_type}
                            </p>
                            <p className="text-xs text-slate-400">Agent ID: {agent.agent_id}</p>
                            {agent.vapi_agent_id && (
                              <p className="text-xs text-slate-400 font-mono break-all">
                                VAPI ID: {agent.vapi_agent_id}
                              </p>
                            )}
                            {agent.phone_number && (
                              <p className="text-xs text-slate-400">{agent.phone_number}</p>
                            )}
                            {getRemainingMessageCount(agent) !== null && (
                              <p className="text-xs text-slate-500 mt-1">
                                {formatCount(getRemainingMessageCount(agent))} messages remaining
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getAgentPlanName(agent) && (
                            <Badge variant="secondary">{getAgentPlanName(agent)}</Badge>
                          )}
                          <Badge variant={agent.status === 'active' ? 'default' : 'outline'}>
                            {agent.status}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={() => openSubscriptionDialog(agent)}>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Subscription
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/service/agents/${agent.agent_id}/configure`)}
                          >
                            Configure
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              navigate(
                                `/service/businesses/${businessId}/twilio?agentId=${encodeURIComponent(agent.agent_id)}`
                              )
                            }
                          >
                            Twilio Config
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packages Tab */}
        <TabsContent value="packages" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Minute Packages</CardTitle>
              <Button onClick={() => setShowPackageDialog(true)} size="sm" className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Package
              </Button>
            </CardHeader>
            <CardContent>
              {packages.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No packages added yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.user_package_id}
                      className="p-4 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">{pkg.package_name}</h4>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span>{formatMinutes(pkg.minutes_remaining)} remaining</span>
                            <span>of {formatMinutes(pkg.minutes_total)}</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Expires: {formatDate(pkg.expires_at)}
                          </p>
                        </div>
                        <Badge variant={pkg.status === 'active' ? 'default' : 'outline'}>
                          {pkg.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Agent Dialog */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Agent</DialogTitle>
            <DialogDescription>Add a new voice agent for this business</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Agent Name *</Label>
              <Input
                value={agentForm.name}
                onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                placeholder="Reception AI"
              />
            </div>
            <div className="grid gap-2">
              <Label>Industry Type</Label>
              <Select value={agentForm.industry_type} onValueChange={(v) => setAgentForm({ ...agentForm, industry_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="plumbing">Plumbing</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="legal">Legal</SelectItem>
                  <SelectItem value="real_estate">Real Estate</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Phone Number</Label>
              <Input
                value={agentForm.phone_number}
                onChange={(e) => setAgentForm({ ...agentForm, phone_number: e.target.value })}
                placeholder="+61 2 0000 0000"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAgentDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAgent} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? 'Creating...' : 'Create Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Minutes Package</DialogTitle>
            <DialogDescription>Add minutes to this business account</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Select Package</Label>
              <Select value={packageForm.package_id} onValueChange={(v) => setPackageForm({ ...packageForm, package_id: v, minutes: '' })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a package or enter custom" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Custom Minutes</SelectItem>
                  {availablePackages.map((pkg) => (
                    <SelectItem key={pkg.package_id} value={pkg.package_id}>
                      {pkg.name} - {pkg.minutes} min (${pkg.price})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!packageForm.package_id && (
              <div className="grid gap-2">
                <Label>Custom Minutes</Label>
                <Input
                  type="number"
                  value={packageForm.minutes}
                  onChange={(e) => setPackageForm({ ...packageForm, minutes: e.target.value })}
                  placeholder="Enter minutes"
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Input
                value={packageForm.notes}
                onChange={(e) => setPackageForm({ ...packageForm, notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackageDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPackage} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? 'Adding...' : 'Add Package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Subscription Dialog */}
      <Dialog open={showSubscriptionDialog} onOpenChange={setShowSubscriptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Subscription</DialogTitle>
            <DialogDescription>
              Assign a subscription plan to {selectedAgent?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Select Plan</Label>
              <Select value={subscriptionPlanId} onValueChange={setSubscriptionPlanId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.name} - ${plan.price}/{plan.billing_cycle} ({plan.message_count ?? plan.minutes_per_month} messages)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignSubscription} disabled={saving || !subscriptionPlanId} className="bg-amber-600 hover:bg-amber-700">
              {saving ? 'Assigning...' : 'Assign Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
